import { useState, useEffect } from 'react';
import { Drawer, Button, Form, Input, Space, Modal, DatePicker, message, InputNumber, Alert, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { saveHotelApi, deleteHotelApi, updateHotelApi, getHotelInfoApi, updateHotelTimeApi, getHotelListDayApi, saveHotelRoomTypesApi, deleteHotelRoomTypesApi, updateHotelRoomTypesApi } from '@/services/Guest';
import './index.less';

const { RangePicker } = DatePicker;
const { Text } = Typography;

export default function HotelManagementDrawer({ open, onClose, conferenceId, conference, getConferenceInfo }) {
    const [hotelForm] = Form.useForm();
    const [dateForm] = Form.useForm();
    const [roomForm] = Form.useForm();
    const [roomManageForm] = Form.useForm();

    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);

    // 弹窗状态
    const [hotelModalVisible, setHotelModalVisible] = useState(false);
    const [dateModalVisible, setDateModalVisible] = useState(false);
    const [roomModalVisible, setRoomModalVisible] = useState(false);
    const [roomManageModalVisible, setRoomManageModalVisible] = useState(false);

    // 当前编辑的数据
    const [currentHotel, setCurrentHotel] = useState(null);
    const [currentRoom, setCurrentRoom] = useState(null);

    // 房型管理表格数据
    const [roomTableData, setRoomTableData] = useState([]);

    // 计算酒店用房量统计
    const getHotelStatistics = () => {
        return tableData
            .map(hotel => {
                // 计算每个酒店下所有日期的所有房型的 roomUsage 之和
                const totalRooms = (hotel.roomTypeDays || []).reduce((sum, dayItem) => {
                    // 计算当前日期下所有房型的 roomUsage 之和
                    const dayTotal = (dayItem.roomInfo || []).reduce((daySum, roomInfo) => {
                        return daySum + (roomInfo.roomUsage || 0);
                    }, 0);
                    return sum + dayTotal;
                }, 0);
                return `${hotel.name}-用房量：${totalRooms}间`;
            })
            .join('；');
    };

    // 加载酒店数据
    useEffect(() => {
        if (open) {
            loadHotelData();
        }
    }, [open]);

    // 加载酒店数据
    const loadHotelData = async () => {
        try {
            setLoading(true);
            const res = await getHotelListDayApi({ conferenceId });
            if (res.status_code === 200 && res.data) {
                setTableData(res.data);
            }
        } catch (error) {
            message.error('加载酒店数据失败');
        } finally {
            setLoading(false);
        }
    };

    // 新增/编辑酒店
    const showHotelModal = (hotel = null) => {
        setCurrentHotel(hotel);
        if (hotel) {
            hotelForm.setFieldsValue({
                hotelName: hotel.name,
                hotelAddress: hotel.address,
            });
        } else {
            hotelForm.resetFields();
        }
        setHotelModalVisible(true);
    };

    const handleHotelOk = async () => {
        try {
            const values = await hotelForm.validateFields();

            if (currentHotel) {
                // 编辑酒店
                const res = await updateHotelApi({
                    id: currentHotel.id,
                    name: values.hotelName,
                    address: values.hotelAddress,
                });

                if (res.status_code === 200) {
                    message.success('编辑酒店成功');
                    loadHotelData();
                    setHotelModalVisible(false);
                    hotelForm.resetFields();
                } else {
                    message.error(res.message || '编辑酒店失败');
                }
            } else {
                // 新增酒店
                const res = await saveHotelApi({
                    conferenceId,
                    name: values.hotelName,
                    address: values.hotelAddress,
                });

                if (res.status_code === 200) {
                    message.success('新增酒店成功');
                    loadHotelData();
                    setHotelModalVisible(false);
                    hotelForm.resetFields();
                } else {
                    message.error(res.message || '新增酒店失败');
                }
            }
        } catch (error) {
            console.error('保存酒店失败:', error);
        }
    };

    const handleDateOk = async () => {
        try {
            const values = await dateForm.validateFields();
            const [startDate, endDate] = values.dateRange;

            const res = await updateHotelTimeApi({
                id: conferenceId,
                hotelStartDate: startDate.format('YYYY-MM-DD'),
                hotelEndDate: endDate.format('YYYY-MM-DD'),
            });

            if (res.status_code === 200) {
                message.success('用房日期修改成功');
                loadHotelData();
                getConferenceInfo(conferenceId);
                setDateModalVisible(false);
                dateForm.resetFields();
            } else {
                message.error(res.message || '用房日期修改失败');
            }
        } catch (error) {
            console.error('修改用房日期失败:', error);
        }
    };

    // 新增/编辑房型 - 打开房型管理弹窗
    const showRoomManageModal = async hotel => {
        setCurrentHotel(hotel);

        if (hotel) {
            // 编辑酒店 - 加载房型数据
            try {
                setLoading(true);
                const res = await getHotelInfoApi({ hotelId: hotel.id });

                if (res.status_code === 200 && res.data) {
                    const hotelData = res.data;

                    // 转换房型数据为表格所需格式
                    const roomData = (hotelData.roomTypes || []).map(room => {
                        const roomDayNum = JSON.parse(room.roomDayNum || '[]');
                        return {
                            id: room.id,
                            roomType: room.roomType,
                            roomDayNum: roomDayNum,
                        };
                    });

                    setRoomTableData(roomData);

                    roomManageForm.setFieldsValue({
                        hotelName: hotelData.name,
                        hotelAddress: hotelData.address,
                    });
                } else {
                    message.error('加载房型数据失败');
                }
            } catch (error) {
                message.error('加载房型数据失败');
            } finally {
                setLoading(false);
            }
        } else {
            // 新增酒店 - 清空数据
            setRoomTableData([]);
            roomManageForm.resetFields();
        }

        setRoomManageModalVisible(true);
    };

    const handleRoomManageOk = () => {
        // TODO: 保存房型数据
        message.success('保存成功');
        setRoomManageModalVisible(false);
    };

    // 打开用房日期管理弹窗（回显日期）
    const showDateManageModal = () => {
        if (conference) {
            const { hotelStartDate, hotelEndDate } = conference || {};
            if (hotelStartDate && hotelEndDate) {
                const start = dayjs(hotelStartDate);
                const end = dayjs(hotelEndDate);
                if (start.isValid() && end.isValid()) {
                    dateForm.setFieldsValue({
                        dateRange: [start, end],
                    });
                }
            }
        }
        setDateModalVisible(true);
    };

    // 新增房型行 - 改为打开弹窗
    const showAddRoomModal = () => {
        setCurrentRoom(null);
        roomForm.resetFields();
        setRoomModalVisible(true);
    };

    // 编辑房型 - 打开弹窗
    const showEditRoomModal = record => {
        setCurrentRoom(record);

        // 回显房型名称
        const formValues = {
            roomType: record.roomType,
        };

        // 回显各个日期的房量
        if (record.roomDayNum && Array.isArray(record.roomDayNum)) {
            record.roomDayNum.forEach(dayInfo => {
                // 使用日期作为表单字段名
                formValues[dayInfo.day] = dayInfo.count;
            });
        }

        roomForm.setFieldsValue(formValues);
        setRoomModalVisible(true);
    };

    // 保存单个房型
    const handleSingleRoomOk = async () => {
        try {
            const values = await roomForm.validateFields();
            const { roomType, ...dateValues } = values;

            // 将日期值转换为 roomDayNum 格式
            const roomDayNum = Object.entries(dateValues)
                .filter(([_, count]) => count !== undefined && count !== null)
                .map(([day, count]) => ({
                    day,
                    count: Number(count),
                }));

            const roomDayNumStr = JSON.stringify(roomDayNum);

            if (currentRoom) {
                // 编辑房型 - 需要二次确认
                Modal.confirm({
                    title: '提示',
                    content: '修改房型数量可能会导致嘉宾原选房型数量失效。请在保存后，重新核对并确认专家排房信息。',
                    okText: '确认',
                    cancelText: '取消',
                    centered: true,
                    onOk: async () => {
                        const res = await updateHotelRoomTypesApi({
                            id: currentRoom.id,
                            hotelId: currentHotel.id,
                            roomType,
                            roomDayNum: roomDayNumStr,
                        });

                        if (res.status_code === 200) {
                            message.success('保存成功');
                            // 刷新酒店列表数据
                            await loadHotelData();
                            // 重新加载房型数据
                            const updatedHotel = tableData.find(h => h.id === currentHotel.id);
                            if (updatedHotel) {
                                await showRoomManageModal(updatedHotel);
                            }
                            setRoomModalVisible(false);
                            roomForm.resetFields();
                        } else {
                            message.error(res.message || '编辑失败');
                        }
                    },
                });
            } else {
                // 新增房型 - 直接保存
                const res = await saveHotelRoomTypesApi({
                    hotelId: currentHotel.id,
                    roomType,
                    roomDayNum: roomDayNumStr,
                });

                if (res.status_code === 200) {
                    message.success('保存成功');
                    // 刷新酒店列表数据
                    await loadHotelData();
                    // 重新加载房型数据
                    const updatedHotel = tableData.find(h => h.id === currentHotel.id);
                    if (updatedHotel) {
                        await showRoomManageModal(updatedHotel);
                    }
                    setRoomModalVisible(false);
                    roomForm.resetFields();
                } else {
                    message.error(res.message || '保存失败');
                }
            }
        } catch (error) {
            console.error('保存房型失败:', error);
        }
    };

    // 保存并添加下一个房型
    const handleSaveAndAddNext = async () => {
        try {
            const values = await roomForm.validateFields();
            const { roomType, ...dateValues } = values;

            // 将日期值转换为 roomDayNum 格式
            const roomDayNum = Object.entries(dateValues)
                .filter(([_, count]) => count !== undefined && count !== null)
                .map(([day, count]) => ({
                    day,
                    count: Number(count),
                }));

            const roomDayNumStr = JSON.stringify(roomDayNum);

            // 新增房型
            const res = await saveHotelRoomTypesApi({
                hotelId: currentHotel.id,
                roomType,
                roomDayNum: roomDayNumStr,
            });

            if (res.status_code === 200) {
                message.success('保存成功，请继续添加下一个房型');
                // 刷新酒店列表数据
                await loadHotelData();
                // 重新加载房型数据
                const updatedHotel = tableData.find(h => h.id === currentHotel.id);
                if (updatedHotel) {
                    setCurrentHotel(updatedHotel);
                    // 重新加载房型表格数据
                    const hotelRes = await getHotelInfoApi({ hotelId: updatedHotel.id });
                    if (hotelRes.status_code === 200 && hotelRes.data) {
                        const hotelData = hotelRes.data;
                        const roomData = (hotelData.roomTypes || []).map(room => {
                            const roomDayNum = JSON.parse(room.roomDayNum || '[]');
                            return {
                                id: room.id,
                                roomType: room.roomType,
                                roomDayNum: roomDayNum,
                            };
                        });
                        setRoomTableData(roomData);
                    }
                }
                // 清空表单准备添加下一个
                roomForm.resetFields();
            } else {
                message.error(res.message || '保存失败');
            }
        } catch (error) {
            console.error('保存房型失败:', error);
        }
    };

    // 删除房型
    const handleDeleteRoom = record => {
        Modal.confirm({
            title: '提示',
            content: '若已有专家选择了该房型，其相关信息将被同步清空。请在确认本操作后，重新核对并完成专家的排房信息。',
            okText: '确认',
            cancelText: '取消',
            centered: true,
            onOk: async () => {
                try {
                    const res = await deleteHotelRoomTypesApi({
                        conferenceId,
                        hotelId: currentHotel.id,
                        roomTypeId: record.id,
                    });

                    if (res.status_code === 200) {
                        message.success('删除成功');
                        // 重新加载房型数据
                        await showRoomManageModal(currentHotel);
                        // 刷新主列表数据
                        loadHotelData();
                    } else {
                        message.error(res.message || '删除失败');
                    }
                } catch (error) {
                    message.error('删除失败');
                }
            },
        });
    };

    // 删除酒店
    const confirmDeleteHotel = hotel => {
        Modal.confirm({
            title: '提示',
            content: '若已有专家选择了该酒店及房型，其相关信息将被同步清空。请在确认本操作后，重新核对并完成专家的排房信息。',
            okText: '确认',
            cancelText: '取消',
            centered: true,
            onOk: async () => {
                try {
                    const res = await deleteHotelApi({
                        conferenceId,
                        hotelId: hotel.id,
                    });

                    if (res.status_code === 200) {
                        message.success('删除酒店成功');
                        loadHotelData();
                    } else {
                        message.error(res.message || '删除酒店失败');
                    }
                } catch (error) {
                    message.error('删除酒店失败');
                }
            },
        });
    };

    // 删除日期
    const confirmDeleteDate = (hotel, date) => {
        Modal.confirm({
            title: '提示',
            content: '起始日期默认为会议项目最早开始日期前一天，截止日期默认为会议项目最晚结束日期加一天；用户若修改过日期，修改用房日期可能会导致已登记专家的原选房型数量丢失。请在保存后，重新核对并开始已排房信息。',
            okText: '确认',
            cancelText: '取消',
            centered: true,
            onOk: () => {
                setTableData(
                    tableData.map(h => {
                        if (h.id === hotel.id) {
                            const hotelDates = h.dates || []; // 确保 dates是数组
                            return {
                                ...h,
                                dates: hotelDates.filter(d => d.id !== date.id),
                            };
                        }
                        return h;
                    }),
                );
                message.success('移除用房日期成功');
            },
        });
    };

    const handleClose = () => {
        onClose();
    };

    // 生成表格列
    const generateColumns = () => {
        // 从 tableData 中莸取所有日期
        const allDates = new Set();
        tableData.forEach(hotel => {
            if (hotel.roomTypeDays) {
                hotel.roomTypeDays.forEach(day => {
                    allDates.add(day.day);
                });
            }
        });

        // 将日期排序（若为空则回退到第一个酒店的 usageStartTime ~ usageEndTime 范围）
        let sortedDates = Array.from(allDates).sort();
        if (sortedDates.length === 0 && tableData.length > 0) {
            const firstHotel = tableData[0];
            const { usageStartTime, usageEndTime } = firstHotel || {};
            if (usageStartTime && usageEndTime) {
                const start = dayjs(usageStartTime);
                const end = dayjs(usageEndTime);
                if (start.isValid() && end.isValid() && !end.isBefore(start, 'day')) {
                    const fallbackDates = [];
                    let cursor = start.clone();
                    while (cursor.isSameOrBefore(end, 'day')) {
                        fallbackDates.push(cursor.format('YYYY-MM-DD'));
                        cursor = cursor.add(1, 'day');
                    }
                    sortedDates = fallbackDates;
                }
            }
        }

        // 生成日期列
        const dateColumns = sortedDates.map(date => ({
            title: `${dayjs(date).format('YYYY-MM-DD')}房型房量`,
            dataIndex: date,
            key: date,
            width: 200,
            render: (text, record) => {
                // 查找该酒店在该日期的房型信息
                const dayData = (record.roomTypeDays || []).find(d => d.day === date);
                const roomInfo = dayData?.roomInfo || [];

                return (
                    <div className="room-list">
                        {roomInfo.length > 0 ? (
                            roomInfo.map((item, index) => (
                                <div key={index} className="room-item">
                                    {item.roomType}：总量{item.roomNum} | 已用{item.roomUsage}
                                </div>
                            ))
                        ) : (
                            <Text type="secondary">&ndash;</Text>
                        )}
                    </div>
                );
            },
        }));

        return [
            {
                title: '基本信息',
                dataIndex: 'baseInfo',
                key: 'baseInfo',
                width: 250,
                fixed: 'left',
                render: (text, record) => (
                    <div className="hotel-base-info">
                        <div className="info-line">
                            <span className="label">名称：</span>
                            <span className="value">{record.name || <Text type="secondary">&ndash;</Text>}</span>
                        </div>
                        <div className="info-line">
                            <span className="label">地址：</span>
                            <span className="value">{record.address || <Text type="secondary">&ndash;</Text>}</span>
                        </div>
                    </div>
                ),
            },
            ...dateColumns,
            {
                title: '操作',
                key: 'action',
                width: 250,
                fixed: 'right',
                render: (text, record) => (
                    <Space size="small">
                        <Button type="link" size="small" onClick={() => showRoomManageModal(record)}>
                            管理房型房量
                        </Button>
                        <Button type="link" size="small" onClick={() => showHotelModal(record)}>
                            编辑酒店
                        </Button>
                        <Button type="link" size="small" danger onClick={() => confirmDeleteHotel(record)}>
                            删除
                        </Button>
                    </Space>
                ),
            },
        ];
    };

    return (
        <>
            <Drawer title="酒店管理" size="100%" placement="top" onClose={handleClose} open={open} className="hotel-management-drawer" closable={{ placement: 'end' }}>
                <div className="hotel-management-content">
                    {tableData.length > 0 && <Alert title={getHotelStatistics()} type="info" style={{ marginBottom: 16 }} />}

                    <div style={{ marginBottom: 16, display: 'flex', gap: '12px' }}>
                        <Button type="primary" onClick={() => showHotelModal(null)}>
                            新增酒店
                        </Button>
                        <Button type="primary" onClick={showDateManageModal}>
                            用房日期管理
                        </Button>
                    </div>

                    <Table columns={generateColumns()} dataSource={tableData} rowKey="id" pagination={false} bordered scroll={{ x: 'max-content' }} size="small" loading={loading} />
                </div>
            </Drawer>

            {/* 新增/编辑酒店弹窗 */}
            <Modal title={currentHotel ? '编辑酒店' : '新增酒店'} open={hotelModalVisible} onOk={handleHotelOk} onCancel={() => setHotelModalVisible(false)} okText="确定" cancelText="取消">
                <Form form={hotelForm} layout="vertical">
                    <Form.Item
                        label="酒店名称"
                        name="hotelName"
                        rules={[
                            { required: true, message: '请输入酒店名称' },
                            { max: 100, message: '酒店名称最多100个字符' },
                        ]}
                    >
                        <Input placeholder="请输入酒店名称" maxLength={100} showCount />
                    </Form.Item>
                    <Form.Item
                        label="酒店地址"
                        name="hotelAddress"
                        rules={[
                            { required: true, message: '请输入酒店地址' },
                            { max: 200, message: '酒店地址最多200个字符' },
                        ]}
                    >
                        <Input placeholder="请输入酒店地址" maxLength={200} showCount />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 用房日期管理弹窗 */}
            <Modal title="用房日期管理" open={dateModalVisible} onOk={handleDateOk} onCancel={() => setDateModalVisible(false)} okText="保存" cancelText="取消" width={600} centered>
                <Form form={dateForm} layout="vertical">
                    <Form.Item label="用房起止日期" name="dateRange" rules={[{ required: true, message: '请选择用房日期' }]} extra={<span style={{ color: '#FAAD14' }}>该日期为当前会期内全部酒店共用日期</span>}>
                        <RangePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder={['开始日期', '结束日期']} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 房型管理弹窗 */}
            <Modal title="管理房型房量" open={roomManageModalVisible} onCancel={() => setRoomManageModalVisible(false)} width={900} centered className="room-manage-modal" footer={null}>
                <div className="room-manage-content">
                    <Form form={roomManageForm} layout="horizontal" labelCol={{ span: 2 }}>
                        <Form.Item label="酒店名称" className="hotel-info-item">
                            <span>{currentHotel?.name || '-'}</span>
                        </Form.Item>
                        <Form.Item label="酒店地址" className="hotel-info-item">
                            <span>{currentHotel?.address || '-'}</span>
                        </Form.Item>
                        <Form.Item label="房型/房量" style={{ marginBottom: 0 }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ textAlign: 'left', marginBottom: '16px' }}>
                                    <Button type="primary" icon={<i className="bi bi-plus"></i>} onClick={showAddRoomModal}>
                                        新增房型
                                    </Button>
                                </div>
                                <Table
                                    dataSource={roomTableData}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                    bordered
                                    scroll={{ y: 400, x: 'max-content' }}
                                    style={{ width: '100%', marginLeft: 0 }}
                                    loading={loading}
                                    columns={(() => {
                                        // 房型管理表头日期优先取当前酒店的 roomTypeDays；若为空则降级取所有酒店；再为空则回退第一个酒店 usageStartTime ~ usageEndTime
                                        const allDates = new Set();

                                        if (currentHotel && Array.isArray(currentHotel.roomTypeDays) && currentHotel.roomTypeDays.length > 0) {
                                            currentHotel.roomTypeDays.forEach(day => {
                                                allDates.add(day.day);
                                            });
                                        } else {
                                            tableData.forEach(hotel => {
                                                if (hotel.roomTypeDays) {
                                                    hotel.roomTypeDays.forEach(day => {
                                                        allDates.add(day.day);
                                                    });
                                                }
                                            });
                                        }

                                        let sortedDates = Array.from(allDates).sort();

                                        // 若仍无日期则使用第一个酒店的 usageStartTime ~ usageEndTime 生成日期列
                                        if (sortedDates.length === 0 && tableData.length > 0) {
                                            const firstHotel = tableData[0];
                                            const { usageStartTime, usageEndTime } = firstHotel || {};
                                            if (usageStartTime && usageEndTime) {
                                                const start = dayjs(usageStartTime);
                                                const end = dayjs(usageEndTime);
                                                if (start.isValid() && end.isValid() && !end.isBefore(start, 'day')) {
                                                    const fallbackDates = [];
                                                    let cursor = start.clone();
                                                    while (cursor.isSameOrBefore(end, 'day')) {
                                                        fallbackDates.push(cursor.format('YYYY-MM-DD'));
                                                        cursor = cursor.add(1, 'day');
                                                    }
                                                    sortedDates = fallbackDates;
                                                }
                                            }
                                        }

                                        const dateColumns = sortedDates.map(date => ({
                                            title: `${dayjs(date).format('YYYY-MM-DD')}房量`,
                                            dataIndex: date,
                                            key: date,
                                            width: 120,
                                            render: (_, record) => {
                                                const dayInfo = (record.roomDayNum || []).find(d => d.day === date);
                                                return dayInfo && dayInfo.count ? dayInfo.count : 0;
                                            },
                                        }));

                                        return [
                                            {
                                                title: '房型名称',
                                                dataIndex: 'roomType',
                                                width: 120,
                                                fixed: 'left',
                                            },
                                            ...dateColumns,
                                            {
                                                title: '操作',
                                                width: 120,
                                                fixed: 'right',
                                                render: (_, record) => (
                                                    <Space>
                                                        <Button type="link" size="small" onClick={() => showEditRoomModal(record)}>
                                                            编辑
                                                        </Button>
                                                        <Button type="link" size="small" danger onClick={() => handleDeleteRoom(record)}>
                                                            移除
                                                        </Button>
                                                    </Space>
                                                ),
                                            },
                                        ];
                                    })()}
                                />
                            </div>
                        </Form.Item>
                    </Form>
                </div>
            </Modal>

            {/* 新增/编辑单个房型弹窗 */}
            <Modal
                title={currentRoom ? '编辑房型' : '新增房型'}
                open={roomModalVisible}
                onCancel={() => {
                    setRoomModalVisible(false);
                    roomForm.resetFields();
                    setCurrentRoom(null);
                }}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => {
                            setRoomModalVisible(false);
                            roomForm.resetFields();
                            setCurrentRoom(null);
                        }}
                    >
                        取消
                    </Button>,
                    currentRoom ? (
                        // 编辑模式：只显示保存按钮
                        <Button key="save" type="primary" onClick={handleSingleRoomOk}>
                            保存
                        </Button>
                    ) : (
                        // 新增模式：显示保存和保存并添加下一个
                        [
                            <Button key="save" type="primary" onClick={handleSingleRoomOk}>
                                保存
                            </Button>,
                            <Button key="saveAndNext" type="primary" onClick={handleSaveAndAddNext}>
                                保存并添加下一个房型
                            </Button>,
                        ]
                    ),
                ]}
                width={500}
                centered
                style={{ maxHeight: '80vh' }}
                styles={{ body: { maxHeight: 'calc(80vh - 110px)', overflowY: 'auto' } }}
            >
                <Form form={roomForm} layout="vertical">
                    <Form.Item
                        label="房型名称"
                        name="roomType"
                        rules={[
                            { required: true, message: '请输入房型名称' },
                            { max: 100, message: '房型名称最多100个字符' },
                        ]}
                    >
                        <Input placeholder="房型名称" maxLength={100} showCount />
                    </Form.Item>

                    {/* 动态生成日期输入项 */}
                    {(() => {
                        // 房型新增/编辑弹窗日期输入：优先取当前酒店的 roomTypeDays；为空则取所有酒店；仍为空则回退第一个酒店 usageStartTime ~ usageEndTime
                        const allDates = new Set();

                        if (currentHotel && Array.isArray(currentHotel.roomTypeDays) && currentHotel.roomTypeDays.length > 0) {
                            currentHotel.roomTypeDays.forEach(day => {
                                allDates.add(day.day);
                            });
                        } else {
                            tableData.forEach(hotel => {
                                if (hotel.roomTypeDays) {
                                    hotel.roomTypeDays.forEach(day => {
                                        allDates.add(day.day);
                                    });
                                }
                            });
                        }

                        let sortedDates = Array.from(allDates).sort();

                        if (sortedDates.length === 0 && tableData.length > 0) {
                            const firstHotel = tableData[0];
                            const { usageStartTime, usageEndTime } = firstHotel || {};
                            if (usageStartTime && usageEndTime) {
                                const start = dayjs(usageStartTime);
                                const end = dayjs(usageEndTime);
                                if (start.isValid() && end.isValid() && !end.isBefore(start, 'day')) {
                                    const fallbackDates = [];
                                    let cursor = start.clone();
                                    while (cursor.isSameOrBefore(end, 'day')) {
                                        fallbackDates.push(cursor.format('YYYY-MM-DD'));
                                        cursor = cursor.add(1, 'day');
                                    }
                                    sortedDates = fallbackDates;
                                }
                            }
                        }

                        return sortedDates.map(date => (
                            <Form.Item key={date} label={`${dayjs(date).format('YYYY-MM-DD')}房量`} name={date}>
                                <InputNumber min={0} defaultValue={0} placeholder="房间数量" style={{ width: '100%' }} />
                            </Form.Item>
                        ));
                    })()}
                </Form>
            </Modal>
        </>
    );
}
